import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import { checkProfileComplete } from '../services/database';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/home';
  const { login, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);

      // Check if profile is complete
      const { currentUser } = await import('../services/firebase').then((m) => ({
        currentUser: m.auth?.currentUser ?? null,
      }));
      if (currentUser) {
        const isComplete = await checkProfileComplete(currentUser.uid);
        if (isComplete) {
          // Redirect to the requested page or home
          navigate(redirectPath);
        } else {
          // Need to complete profile first, then redirect
          navigate(`/complete-profile?redirect=${encodeURIComponent(redirectPath)}`);
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      await resetPassword(resetEmail);
      toast.success('Password reset email sent!');
      setShowResetDialog(false);
      setResetEmail('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email');
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <img 
          src="/Untitled design (24).png" 
          alt="Quirke Trophies Logo" 
          className="auth-logo"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/Untitled design (24).png";
          }}
        />
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Sign in to continue</p>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <div className="password-field-wrapper">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              <img
                src={showPassword ? '/assets/images/hide.png' : '/assets/images/view.png'}
                alt={showPassword ? 'Hide password' : 'Show password'}
                className="password-toggle-icon"
              />
            </button>
          </div>

          <button
            type="button"
            className="forgot-password"
            onClick={() => setShowResetDialog(true)}
          >
            Forgot Password?
          </button>

          <Button type="submit" fullWidth loading={loading} className="login-submit">
            Login
          </Button>
        </form>

        <div className="register-link">
          <span>Don't have an account? </span>
          <Link to={`/register${redirectPath !== '/home' ? `?redirect=${encodeURIComponent(redirectPath)}` : ''}`}>Sign Up</Link>
        </div>
      </div>

      {showResetDialog && (
        <div className="modal-overlay" onClick={() => setShowResetDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reset Password</h2>
            <p>Enter your email address and we'll send you a link to reset your password.</p>
            <Input
              type="email"
              placeholder="Enter your email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleResetPassword}>
                Send Reset Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
